import React from "react";
import type { InstructorStat, StatsDateRange } from "../../../api/stats/Stats.typed";
import { useStats } from "../../../contexts/api";
import { INSTRUCTOR_FILTER_ALL } from "../../common/InstructorEncadrantFilter";

export type InstructorOption = Pick<InstructorStat, "id" | "first_name" | "last_name">;

function sortInstructorOptions(opts: InstructorOption[]): InstructorOption[] {
  return [...opts].sort((a, b) => {
    const ln = a.last_name.localeCompare(b.last_name, "fr");
    if (ln !== 0) return ln;
    return a.first_name.localeCompare(b.first_name, "fr");
  });
}

function mergeOptions(prev: InstructorOption[], rows: InstructorStat[]): InstructorOption[] {
  const byId = new Map<number, InstructorOption>(prev.map((o) => [o.id, o]));
  for (const r of rows) {
    if (r.id > 0 && !byId.has(r.id)) {
      byId.set(r.id, { id: r.id, first_name: r.first_name, last_name: r.last_name });
    }
  }
  return sortInstructorOptions(Array.from(byId.values()));
}

export function useInstructorStatsData(range: StatsDateRange) {
  const { getInstructorStats } = useStats();
  const [instructorId, setInstructorId] = React.useState<number>(INSTRUCTOR_FILTER_ALL);
  const [options, setOptions] = React.useState<InstructorOption[]>([]);
  const [rows, setRows] = React.useState<InstructorStat[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setInstructorId(INSTRUCTOR_FILTER_ALL);
    setOptions([]);
  }, [range]);

  React.useEffect(() => {
    let stale = false;
    const filterId = instructorId >= 1 ? instructorId : undefined;
    setLoading(true);
    void getInstructorStats(range, filterId).then((data) => {
      if (stale) return;
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setOptions((prev) => mergeOptions(filterId == null ? [] : prev, list));
      setLoading(false);
    });
    return () => {
      stale = true;
    };
  }, [getInstructorStats, instructorId, range]);

  return { rows, options, instructorId, setInstructorId, loading };
}
